import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Check, CircleDot } from "lucide-react";
import { LedgerDrawer } from "@/components/LedgerDrawer";
import { useAuth } from "@/context/AuthContext";

/**
 * 7-department floor pipeline stepper.
 *
 * Given an `order_item_id`, fetches the associated production job (if any) and renders the
 * 7 in-house floor departments in strict sequence, highlighting the current one and marking
 * completed / skipped stages. Compact and horizontally-scrollable on narrow screens.
 *
 * Departments: Casting → Filing → Setting → Polishing → QC → Tagging → Dispatch.
 */
const DEPARTMENTS = [
  { key: "Casting", label: "Casting" },
  { key: "Filing", label: "Filing" },
  { key: "Setting", label: "Setting" },
  { key: "Polish", label: "Polishing" },
  { key: "QC", label: "QC" },
  { key: "Tagging", label: "Tagging" },
  { key: "Dispatch", label: "Dispatch" },
];

export function ProductionStepper({ orderItemId, hidePricing = false }) {
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { user } = useAuth();
  const canEditLedger = ["admin", "super-admin", "manager"].includes(user?.role);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/production/jobs`, { params: {} });
        const rows = Array.isArray(data) ? data : (data?.items || []);
        if (alive) setJob(rows.find((j) => j.order_item_id === orderItemId) || null);
      } catch (err) {
        // Silently ignore — production info is optional for viewers who lack access.
        void err;
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [orderItemId]);

  if (loading) return null;
  if (!job) {
    return (
      <div className="text-xs text-[#4A5A50] italic mt-3" data-testid="stepper-not-started">
        Production not started yet.
      </div>
    );
  }

  const stageByKey = Object.fromEntries((job.stages || []).map((s) => [s.key, s]));
  const currentKey = job.current_stage;
  const currentIdx = DEPARTMENTS.findIndex((d) => d.key === currentKey);

  const stateFor = (deptKey, idx) => {
    const s = stageByKey[deptKey];
    if (s?.status === "skipped") return "skipped";
    if (s?.status === "done") return "done";
    if (deptKey === currentKey) return "current";
    if (currentIdx > -1 && idx < currentIdx) return "done";
    return "pending";
  };

  const totalLoss = (job.weight_ledger || []).reduce((sum, e) => sum + (Number(e.loss_g) || 0), 0);

  return (
    <div className="mt-4" data-testid={`stepper-${orderItemId}`}>
      <div className="text-[10px] uppercase tracking-widest text-[#4A5A50] mb-2 flex items-center justify-between">
        <span>Production Progress</span>
        {!hidePricing && job.weight_ledger?.length > 0 && (
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="normal-case tracking-normal text-[11px] underline decoration-dotted hover:text-[#0F5E39] transition"
            data-testid={`stepper-ledger-open-${orderItemId}`}
          >
            Weight ledger: <strong>{job.weight_ledger.length}</strong> entries
            {totalLoss > 0 ? <span> · loss <strong>{totalLoss.toFixed(2)}g</strong></span> : null}
          </button>
        )}
      </div>
      <div className="overflow-x-auto">
        <div className="flex items-center gap-0 min-w-max">
          {DEPARTMENTS.map((d, i) => {
            const st = stateFor(d.key, i);
            const isLast = i === DEPARTMENTS.length - 1;
            const stage = stageByKey[d.key];
            let bg = "bg-white border-[#E4DFCF] text-[#4A5A50]";
            let icon = <span className="text-[10px] font-semibold">{i + 1}</span>;
            if (st === "done") { bg = "bg-[#0F5E39] border-[#0F5E39] text-white"; icon = <Check size={12}/>; }
            else if (st === "current") { bg = "bg-[#C9A24B] border-[#C9A24B] text-white ring-2 ring-[#C9A24B]/30"; icon = <CircleDot size={12}/>; }
            else if (st === "skipped") { bg = "bg-[#F1EDDF] border-[#E4DFCF] text-[#8A6D2E] italic"; icon = <span className="text-[9px]">N/A</span>; }
            return (
              <React.Fragment key={d.key}>
                <div className="flex flex-col items-center min-w-[60px]" data-testid={`stepper-node-${d.key}`}>
                  <div className={`w-7 h-7 rounded-full border flex items-center justify-center ${bg}`}>{icon}</div>
                  <div className={`text-[10px] mt-1 whitespace-nowrap ${st === "current" ? "font-semibold text-[#0F1E15]" : "text-[#4A5A50]"}`}>{d.label}</div>
                  {st === "skipped" && stage?.remarks && (
                    <div className="text-[9px] text-[#8A6D2E] italic mt-0.5 whitespace-nowrap">{stage.remarks}</div>
                  )}
                </div>
                {!isLast && (
                  <div className={`h-px flex-1 min-w-[16px] ${st === "done" || st === "skipped" ? "bg-[#0F5E39]" : "bg-[#E4DFCF]"}`}/>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
}
